import React, { useCallback, useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useDispatch } from "src/store";
import useFetchInterval from "src/ui/hooks/useFetchInterval";
import useFetchResult from "src/ui/hooks/useFetchResult";

import { createDeviceMdmCommand, deleteDeviceMdmCommand, fetchDeviceMdmCommands } from "@dashboard/devices/api/devices";
import { selectDetailsLicense, selectPaneTab } from "@dashboard/devices/store/selectors";
import { MdmCommand } from "@dashboard/devices/types/commands";
import { hasUnfinishedCommands } from "@dashboard/shared/components/SidePanel/shared/CommandTab/CommandTab.utils";
import { workspacesApi } from "@dashboard/workspaces/api";
import { pageIndexOffset } from "@shared/table/Tanstack.utils";
import { showErrorToast, showSuccessToast } from "@shared/toasts/Toasts";
import { SortingState } from "@tanstack/react-table";

function getSortCriteria(sorting: SortingState) {
    if (!sorting.length) {
        return "created";
    }
    const mapping: Record<string, string> = {
        date: "created",
        status: "status",
        origin: "origin",
        command: "caption",
    };
    return mapping[sorting[0].id] || "created";
}

function getSortDirection(sorting: SortingState) {
    if (!sorting.length || sorting[0].desc) {
        return "descending";
    }
    return "ascending";
}

const rowsPerPage = [10, 25, 50];

const emptyCommands: { list: MdmCommand[]; count: number } = { list: [], count: 0 };

type DeviceCommandContextType = {
    data: MdmCommand[];
    totalCount: number;
    isLoading: boolean;
    deletingId: number | null;
    resendingId: number | null;
    pageIndex: number;
    sorting: SortingState;
    pageSize: number;
    rowsPerPage: number[];
    onCreate: (json: Record<string, unknown>, commandId: number | null) => Promise<void>;
    onDelete: (command: MdmCommand) => Promise<void>;
    setSorting: React.Dispatch<React.SetStateAction<SortingState>>;
    setPageIndex: React.Dispatch<React.SetStateAction<number>>;
    setPageSize: React.Dispatch<React.SetStateAction<number>>;
};

const DeviceCommandContext = React.createContext<DeviceCommandContextType>({
    data: [],
    totalCount: 0,
    isLoading: false,
    deletingId: null,
    resendingId: null,
    sorting: [{ id: "date", desc: true }],
    pageIndex: 0,
    pageSize: rowsPerPage[0],
    rowsPerPage,
    onCreate: () => Promise.resolve(),
    onDelete: () => Promise.resolve(),
    setSorting: () => {},
    setPageIndex: () => {},
    setPageSize: () => {},
});

const refreshableTabs = ["COMMANDS", "BWC"];

export const DeviceCommandProvider = (props: React.PropsWithChildren) => {
    const [sorting, setSorting] = useState<SortingState>([{ id: "date", desc: true }]);
    const [pageIndex, setPageIndex] = useState(0);
    const [pageSize, setPageSize] = useState(rowsPerPage[0]);
    const [deletingId, setDeletingId] = useState<number | null>(null);
    const [resendingId, setResendingId] = useState<number | null>(null);

    const { t } = useTranslation(["common", "sidepanels"]);
    const dispatch = useDispatch();
    const license = useSelector(selectDetailsLicense);
    const sortCriteria = getSortCriteria(sorting);
    const sortDirection = getSortDirection(sorting);

    const selectedTab = useSelector(selectPaneTab);

    const {
        data: commands,
        isLoading,
        setData: setCommands,
    } = useFetchResult(
        [license?.device?.id, pageSize, pageIndex, sortCriteria, sortDirection],
        () =>
            fetchDeviceMdmCommands({
                deviceId: license?.device?.id as number,
                pageSize,
                pageIndex: pageIndex + pageIndexOffset,
                sortCriteria,
                sortDirection,
            })
                .then((response) => ({ list: response.list, count: response.count }))

                .catch(() => emptyCommands),
        { initialData: emptyCommands, skip: !license?.device?.id },
    );
    const data = commands.list;
    const totalCount = commands.count;
    const hasUnfinishedCommand = hasUnfinishedCommands(data);

    const onRefresh = useCallback(async () => {
        if (license?.device?.id) {
            try {
                const response = await fetchDeviceMdmCommands({
                    deviceId: license.device.id,
                    pageSize,
                    pageIndex: pageIndex + pageIndexOffset,
                    sortCriteria,
                    sortDirection,
                });
                setCommands(() => ({ list: response.list, count: response.count }));
            } catch (_error) {
                setCommands(() => emptyCommands);
            }
        }
    }, [license?.device?.id, pageSize, pageIndex, sortCriteria, sortDirection, setCommands]);

    const onCreate = async (json: Record<string, unknown>, commandId: number | null) => {
        if (license?.device?.id) {
            try {
                setResendingId(commandId);

                const newCommands = await createDeviceMdmCommand({ deviceId: license.device.id, json });
                if (newCommands?.length) {
                    setCommands((prev) => ({ list: [...newCommands, ...prev.list], count: prev.count + newCommands.length }));
                    dispatch(workspacesApi.util.invalidateTags(["WorkspaceFeatures"]));
                    showSuccessToast(t("common:toast.commandEnqueued"));
                }
            } catch (err) {
                const error = err as Error;
                showErrorToast(error.message || t("sidepanels:devicePanel.toast.commandSentFailed"));
            } finally {
                setResendingId(null);
            }
        }
    };

    const onDelete = async (command: MdmCommand) => {
        if (license?.device?.id) {
            try {
                setDeletingId(command.id);
                const response = await deleteDeviceMdmCommand({ deviceId: license.device?.id, commandId: command.id });
                if (response) {
                    setCommands((prev) => ({ list: prev.list.filter((cmd) => cmd.id !== command.id), count: prev.count - 1 }));
                    dispatch(workspacesApi.util.invalidateTags(["WorkspaceFeatures"]));
                    showSuccessToast(t("sidepanels:devicePanel.toast.commandDeletedSuccessfully"));
                }
            } catch (err) {
                const error = err as Error;
                showErrorToast(error.message || t("sidepanels:devicePanel.toast.commandDeletedFailed"));
            } finally {
                setDeletingId(null);
            }
        }
    };

    const previousTabRef = React.useRef(selectedTab);

    React.useEffect(() => {
        const previousTab = previousTabRef.current;
        previousTabRef.current = selectedTab;

        // The mount, and every page/sort change, are already fetched by `useFetchResult` above. Only an
        // actual switch into a refreshable tab leaves the parameters untouched and so needs its own refresh.
        if (previousTab !== selectedTab && refreshableTabs.includes(selectedTab)) {
            void onRefresh();
        }
    }, [selectedTab, onRefresh]);

    useFetchInterval(onRefresh, {
        frequency: 10000,
        condition: hasUnfinishedCommand && refreshableTabs.includes(selectedTab),
    });

    return (
        <DeviceCommandContext.Provider
            value={{
                data,
                totalCount,
                isLoading,
                deletingId,
                resendingId,
                sorting,
                pageIndex,
                pageSize,
                rowsPerPage,
                onCreate,
                onDelete,
                setSorting,
                setPageIndex,
                setPageSize,
            }}
        >
            {props.children}
        </DeviceCommandContext.Provider>
    );
};

export const useDeviceCommand = () => {
    const context = useContext(DeviceCommandContext);
    if (!context) {
        throw new Error("useDeviceCommand must be used within a DeviceCommandProvider");
    }
    return context;
};
